import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import SignupForm from './SignupForm';

import { momentumData as data } from './momentum';

const Quadrant = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    // CSV numeric columns and dimensions
    const NUMERIC_CSV_COLUMNS = [
      'MomentumScore',
      'LatestPayGap',
      'XOffset',
      'YOffset',
      'XTooltipOffset',
      'YTooltipOffset',
    ];
    const MARGIN = { top: 60, right: 50, bottom: 40, left: 90 };
    const LABEL_PADDING = { top: 0, right: 40, bottom: 0, left: 0 };

    // Convert numeric columns from strings to numbers.
    data.forEach((d) => NUMERIC_CSV_COLUMNS.forEach((col) => (d[col] = +d[col])));

    const width = 600 - MARGIN.left - MARGIN.right;
    const height = 400 - MARGIN.top - MARGIN.bottom;
    const totalWidth = width + MARGIN.left + MARGIN.right + LABEL_PADDING.left + LABEL_PADDING.right;
    const totalHeight = height + MARGIN.top + MARGIN.bottom + LABEL_PADDING.top + LABEL_PADDING.bottom;

    // Clear any previous content.
    d3.select(containerRef.current).selectAll('*').remove();

    // Create the main svg
    const svg = d3
      .select(containerRef.current)
      .append('svg')
      .attr('width', '100%')
      .attr('viewBox', `${-LABEL_PADDING.left} ${-LABEL_PADDING.top} ${totalWidth} ${totalHeight}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .classed('quadrant-svg', true);

    // Background rectangle (transparent)
    svg.append('rect').attr('width', totalWidth).attr('height', totalHeight).attr('fill', 'hsl(231, 50%, 5%, 0)');

    // Main group (chart) with margins
    const group = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Create scales
    const xExtent = d3.extent(data, (d) => d.LatestPayGap);
    const xScale = d3
      .scaleLinear()
      // Reverse domain so that 0 is toward the right end
      .domain([xExtent[1] + 1, xExtent[0] - 1])
      .range([0, width]);

    const yScale = d3.scaleSymlog().domain([-5, 5]).range([height, 0]).constant(0.1);

    // Quadrants data and helper functions
    const quadrantsData = [
      {
        name: 'Avoid',
        x: 0,
        y: yScale(0),
        width: width / 2,
        height: yScale(-5) - yScale(0),
      },
      {
        name: 'Coast',
        x: width / 2,
        y: yScale(0),
        width: width / 2,
        height: yScale(-5) - yScale(0),
      },
      {
        name: 'Sustain',
        x: 0,
        y: yScale(5),
        width: width / 2,
        height: yScale(0) - yScale(5),
      },
      {
        name: 'Leader',
        x: width / 2,
        y: yScale(5),
        width: width / 2,
        height: yScale(0) - yScale(5),
      },
    ];

    // Helper to create a rectangle
    const createRect = (parent, attrs) => {
      parent
        .append('rect')
        .attr('x', attrs.x)
        .attr('y', attrs.y)
        .attr('width', attrs.width)
        .attr('height', attrs.height)
        .attr('fill', attrs.fill)
        .attr('opacity', attrs.opacity);
    };

    // Helper to create centered text
    const createText = (parent, attrs) => {
      parent
        .append('text')
        .attr('x', attrs.x)
        .attr('y', attrs.y)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .style('text-transform', 'uppercase')
        .style('font-weight', '300')
        .style('font-size', '0.9rem')
        .attr('opacity', attrs.opacity)
        .text(attrs.text);
    };

    // Helper to create gridlines
    const createGridline = (parent, yPosition) => {
      parent
        .append('line')
        .attr('x1', 0)
        .attr('x2', width)
        .attr('y1', yPosition)
        .attr('y2', yPosition)
        .attr('stroke', 'hsl(227, 15%, 55%)')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '2')
        .attr('opacity', 0.3);
    };

    // Helper to create axes
    const createAxis = (axis, transform, className) => {
      group
        .append('g')
        .attr('transform', transform)
        .attr('class', className)
        .call(axis)
        .selectAll('text')
        .style('text-anchor', 'middle')
        .attr('dy', '0em');
    };

    // Helper to create labels
    const createLabel = (parent, attrs) => {
      parent
        .append('text')
        .attr('class', 'label')
        .attr('x', attrs.x)
        .attr('y', attrs.y)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .text(attrs.text)
        .style('font-size', attrs.fontSize)
        .style('font-weight', '600')
        .style('letter-spacing', '0.05rem');
    };

    // Add quadrants (rectangles with text)
    const quadrantsGroup = group.append('g').attr('class', 'quadrants');
    quadrantsGroup
      .selectAll('g.quadrant')
      .data(quadrantsData)
      .enter()
      .append('g')
      .attr('class', 'quadrant')
      .each(function (d, i) {
        createRect(d3.select(this), {
          x: d.x,
          y: d.y,
          width: d.width,
          height: d.height,
          fill: i === 0 ? 'hsl(0, 0%, 0%)' : 'hsl(0, 100%, 100%)',
          opacity: i === 0 || i === 3 ? 0.15 : 0.05,
        });
        createText(d3.select(this), {
          x: d.x + d.width / 2,
          y: d.y + d.height / 2,
          text: d.name,
          opacity: 0.9,
        });
      });

    // Add gridlines
    const gridGroup = group.append('g').attr('class', 'gridlines');
    const numGridlines = 10;
    const gridlinePositions = d3.range(numGridlines + 1).map((i) => (i * height) / numGridlines);
    gridlinePositions.forEach((yPos) => createGridline(gridGroup, yPos));

    // Create axes
    createAxis(d3.axisBottom(xScale).tickSize(0), `translate(0, ${yScale(0)})`, 'axis axis-x');
    createAxis(
      d3
        .axisLeft(yScale)
        .tickValues(gridlinePositions.map((d) => yScale.invert(d)))
        .tickFormat((d) => (d === 0 ? '' : d3.format('.1g')(d)))
        .tickSize(0),
      `translate(${width / 2}, 0)`,
      'axis axis-y',
    );

    // Remove axis paths and style tick text
    d3.selectAll('.axis').select('path').remove();

    d3.selectAll('.axis .tick text').style('fill', 'hsl(227, 9%, 81%)').style('font-size', '0.5rem');

    d3.select('.axis-x').selectAll('.tick text').style('fill', 'hsl(226, 15%, 50%)');
    d3.select('.axis-y').selectAll('.tick text').style('fill', 'hsl(226, 15%, 50%)');

    d3.select('.axis-x')
      .selectAll('g.tick')
      .append('line')
      .attr('y1', -(height - yScale(0)))
      .attr('y2', yScale(0))
      .attr('stroke', 'hsl(227, 15%, 55%)')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '2')
      .attr('opacity', 0.3);

    // Remove any old labels and add new ones
    d3.selectAll('.label').remove();
    const labels = [
      {
        text: 'High Momentum',
        x: width / 2,
        y: -MARGIN.top * 0.45,
        fontSize: '0.5rem',
      },
      {
        text: 'Bias to Action',
        x: width / 2,
        y: -MARGIN.top * 0.26,
        fontSize: '0.4rem',
      },
      {
        text: 'Low Momentum',
        x: width / 2,
        y: height + MARGIN.bottom / 2,
        fontSize: '0.5rem',
      },
      {
        text: 'Laggard',
        x: width / 2,
        y: height + (MARGIN.bottom / 2) * 1.55,
        fontSize: '0.4rem',
      },
      {
        text: 'Big Pay Gap',
        x: -MARGIN.left * 0.45,
        y: height / 2,
        fontSize: '0.5rem',
      },
      {
        text: 'Less Fair',
        x: -MARGIN.left * 0.45,
        y: height * 0.54,
        fontSize: '0.4rem',
      },
      {
        text: 'Small Pay Gap',
        x: width + MARGIN.right * 0.8,
        y: height / 2,
        fontSize: '0.5rem',
      },
      {
        text: 'More Fair',
        x: width + MARGIN.right * 0.8,
        y: height * 0.54,
        fontSize: '0.4rem',
      },
    ];
    labels.forEach((label) => createLabel(group, label));

    // Data group for the data points
    const dataGroup = group.append('g').attr('class', 'data');

    // Collision resolution function
    function resolveCollisions(data, xScale, yScale) {
      const radius = 15; // Minimum separation between points
      const positions = [];

      // Set up initial positions
      data.forEach((d) => {
        positions.push({
          x: xScale(d.LatestPayGap),
          y: yScale(d.MomentumScore),
          originalX: xScale(d.LatestPayGap),
          originalY: yScale(d.MomentumScore),
        });
      });

      // Iterate to resolve collisions
      for (let iteration = 0; iteration < 30; iteration++) {
        let moved = false;
        for (let i = 0; i < positions.length; i++) {
          for (let j = i + 1; j < positions.length; j++) {
            const dx = positions[j].x - positions[i].x;
            const dy = positions[j].y - positions[i].y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance < radius) {
              moved = true;
              let force = 0;
              if (distance === 0) {
                // Give a small random push if the points are exactly on top of each other.
                force = 0.5;
              } else {
                force = ((radius - distance) / distance) * 0.5;
              }
              const moveX = dx * force;
              const moveY = dy * force;

              // Move points apart and then add a slight pull toward the original positions.
              positions[i].x -= moveX;
              positions[i].y -= moveY;
              positions[j].x += moveX;
              positions[j].y += moveY;

              const pullStrength = 0.1;
              positions[i].x += (positions[i].originalX - positions[i].x) * pullStrength;
              positions[i].y += (positions[i].originalY - positions[i].y) * pullStrength;
              positions[j].x += (positions[j].originalX - positions[j].x) * pullStrength;
              positions[j].y += (positions[j].originalY - positions[j].y) * pullStrength;
            }
          }
        }
        if (!moved) break;
      }
      return positions;
    }

    // Resolve collisions and create the data points
    const positions = resolveCollisions(data, xScale, yScale);

    const dataPointsGroup = dataGroup
      .selectAll('g.data-point')
      .data(data)
      .enter()
      .append('g')
      .attr('class', (d) => `data-point ${d.SafeCompanyName || d.Company}`)
      .attr('transform', (d, i) => `translate(${positions[i].x}, ${positions[i].y})`);

    // Append circles for each data point
    dataPointsGroup
      .append('circle')
      .attr('r', 5)
      .attr('fill', (d) => d.CompanyColor || 'steelblue');

    // Append text labels for each data point
    dataPointsGroup
      .append('text')
      .attr('x', (d) => d.XOffset)
      .attr('y', (d) => d.YOffset)
      .attr('class', 'name')
      .text((d) => d.Company)
      .attr('dominant-baseline', 'middle')
      .style('font-size', '0.6rem')
      .style('pointer-events', 'none');

    // Interaction: show tooltip on hover
    dataPointsGroup
      .on('mouseenter', function (event, d) {
        if (isNaN(d.MomentumScore) || isNaN(d.LatestPayGap)) {
          console.error('Invalid data point:', d);
          return;
        }
        const text = d3.select(this).select('text.name');

        text
          .transition()
          .attr('transform', d.XOffset < 0 ? 'translate(-12, 0)' : 'translate(12, 0)')
          .style('fill', d.CompanyColor || 'hsl(230, 29%, 19%)')
          .style('text-shadow', 'none');

        const tooltip = d3
          .select(this)
          .insert('g', ':first-child')
          .attr('class', 'tooltip-graph')
          .attr('opacity', 0)
          .style('pointer-events', 'none');

        // Tooltip background based on the text element's bounding box
        const textElement = text.node();
        const { x, y, width: textWidth, height: textHeight } = textElement.getBBox();

        tooltip
          .append('rect')
          .attr('x', x - 3)
          .attr('y', y - 1.5)
          .attr('width', textWidth + 6)
          .attr('height', textHeight + 3)
          .attr('fill', 'hsl(227, 9%, 81%)')
          .attr('rx', 2)
          .transition()
          .attr('transform', d.XOffset < 0 ? 'translate(-12, 0)' : 'translate(12, 0)');

        // Compute distances for the tooltip lines
        const verticalLineLength = yScale(0) - yScale(d.MomentumScore);
        const horizontalLineLength = xScale(d.LatestPayGap) - width / 2;

        // Tooltip dashed lines
        const dashedLines = tooltip
          .append('g')
          .attr('fill', 'none')
          .attr('stroke', 'hsl(227, 9%, 81%)')
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '7 4')
          .style('animation', 'dashOffset 1.5s linear infinite');

        dashedLines.append('path').attr('d', `M 0 0 v ${verticalLineLength}`);
        dashedLines.append('path').attr('d', `M 0 0 h ${-horizontalLineLength}`);

        // Tooltip labels for the axes values
        const labelsGroup = tooltip.append('g').attr('font-size', '0.6rem').attr('fill', 'hsl(227, 9%, 81%)');

        // Label for Latest Pay Gap (x-axis)
        const labelX = labelsGroup
          .append('g')
          .attr('transform', `translate(0, ${verticalLineLength + d.XTooltipOffset})`);
        const textX = labelX
          .append('text')
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
          .style('fill', 'hsl(230, 29%, 19%)')
          .text(d.LatestPayGap.toFixed(1) + '%');
        const { width: xLabelWidth, height: xLabelHeight } = textX.node().getBBox();
        labelX
          .insert('rect', ':first-child')
          .attr('x', -xLabelWidth / 2 - 4)
          .attr('y', -xLabelHeight / 2 - 2)
          .attr('width', xLabelWidth + 8)
          .attr('height', xLabelHeight + 4)
          .attr('rx', 3);

        // Label for Momentum Score (y-axis)
        const labelY = labelsGroup
          .append('g')
          .attr('transform', `translate(${-horizontalLineLength - d.YTooltipOffset}, 0)`);
        const textY = labelY
          .append('text')
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
          .style('fill', 'hsl(230, 29%, 19%)')
          .text(d.MomentumScore.toFixed(2));
        const { width: yLabelWidth, height: yLabelHeight } = textY.node().getBBox();
        labelY
          .insert('rect', ':first-child')
          .attr('x', -yLabelWidth / 2 - 4)
          .attr('y', -yLabelHeight / 2 - 2)
          .attr('width', yLabelWidth + 8)
          .attr('height', yLabelHeight + 4)
          .attr('rx', 3);

        // Highlight circle animation
        tooltip
          .append('circle')
          .attr('fill', 'hsla(248, 100%, 23%, 0.277)')
          .attr('stroke', 'hsl(227, 9%, 81%)')
          .attr('stroke-width', 2)
          .attr('r', 0)
          .transition()
          .attr('r', 9.5);

        // Show the tooltip
        tooltip.transition().attr('opacity', 1);
      })
      .on('mouseout', function () {
        d3.select(this)
          .select('text.name')
          .transition()
          .delay(100)
          .attr('transform', 'translate(0, 0)')
          .style('fill', 'hsl(227, 9%, 81%)');

        d3.select(this).select('g.tooltip-graph').transition().attr('opacity', 0).remove();
      });

    // Voronoi diagram for improved hover interaction
    const voronoi = d3.Delaunay.from(
      data,
      (d) => xScale(d.LatestPayGap),
      (d) => yScale(d.MomentumScore),
    ).voronoi([0, 0, width, height]);

    const voronoiGroup = group.append('g').attr('class', 'voronoi');

    voronoiGroup
      .selectAll('path')
      .data(data)
      .enter()
      .append('path')
      .attr('d', (d, i) => voronoi.renderCell(i))
      .attr('class', (d) => `voronoi-cell ${d.SafeCompanyName || d.Company}`)
      .style('fill', 'none')
      .style('pointer-events', 'all')
      .on('mouseenter', function (event, d) {
        // Simulate a mouseenter on the corresponding data point.
        const dataPoint = d3.select(`.data-point.${d.SafeCompanyName || d.Company}`);
        if (dataPoint.node()) {
          const simulatedEvent = new Event('mouseenter');
          dataPoint.node().dispatchEvent(simulatedEvent);
        }
      })
      .on('mouseout', function (_event, d) {
        // Simulate a mouseout on the corresponding data point.
        const dataPoint = d3.select(`.data-point.${d.SafeCompanyName || d.Company}`);
        if (dataPoint.node()) {
          const simulatedEvent = new Event('mouseout');
          dataPoint.node().dispatchEvent(simulatedEvent);
        }
      });
  }, []);

  return (
    <div className="intelligence w-full bg-gray-50 py-20 sm:pb-[100px]">
      <div className="content max-w-[1120px] mx-auto">
        <div className="text-indigo-100 font-semibold sm:text-lg">
          Our compensation intelligence is your competitive advantage
        </div>
        <div className="flex flex-col gap-4 mt-8 -ml-1">
          <h3 className="text-4xl sm:text-6xl font-bold tracking-tighter">
            The only compensation platform with built-in competitive intelligence
          </h3>
        </div>
        <div ref={containerRef} className="viz w-full overflow-x-hidden"></div>
        <div className="text-gray-500 text-sm w-full flex flex-row items-center gap-1.5 pl-36">
          <div className="animate-pulse h-3 w-3 bg-indigo-400 rounded-full"></div>
          <div>Updated just now</div>
        </div>
        <div className="mt-6 flex flex-col items-center gap-3 text-center">
          <p className="text-lg text-white-600 max-w-[520px]">
            Explore paygap.fyi, our free public database of 20,000+ companies reporting pay gap data worldwide.
          </p>
          <a
            href="https://paygap.fyi"
            className="whitespace-nowrap rounded-full text-left outline-none ring-0 transition-all focus:border-none focus:outline-none font-medium flex items-center gap-3 px-5 py-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 active:scale-95 duration-200 bg-indigo-600 text-white"
            rel="noopener noreferrer"
          >
            Explore paygap.fyi
          </a>
        </div>
        <div className="flex justify-center mt-8">
          <SignupForm />
        </div>
      </div>
    </div>
  );
};

export default Quadrant;
